import { useTranslations } from '@aikoa/i18n/react'
import { useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { useShallow } from 'zustand/react/shallow'
import PropertySelect from '~/components/assets/PropertySelect'
import CloseButton from '~/components/CloseButton'
import { useLeadStore } from '~/components/leads/leadStore'
import { validateLeadMatchScores } from '~/components/leads/utils'
import { Label } from '~/components/typography/Content'
import { useUserOwnAssets } from '~/data/assets/useAssets'
import { useMatchingAssets } from '~/data/leads/useLeads'
import { cn } from '~/lib/utils'

export function LeadOfferedAsset({
  className,
  leadId,
  size = 'sm',
  allowReset = true,
  rejectedAssetIds,
}: {
  className?: string
  leadId?: string
  size?: 'sm' | 'default'
  allowReset?: boolean
  rejectedAssetIds?: string[]
}) {
  const { t } = useTranslations('leads')
  const { offerAssetId, setOfferAssetId, reset, resetManualFilter } = useLeadStore(
    useShallow((state) => ({
      offerAssetId: state.offerAssetId,
      setOfferAssetId: state.setOfferAssetId,
      reset: state.reset,
      resetManualFilter: state.resetManualFilter,
    }))
  )

  const onSetOfferedAsset = useCallback(
    (assetId: string) => {
      resetManualFilter()
      setOfferAssetId(assetId)
    },
    [setOfferAssetId, resetManualFilter]
  )

  const { assets, isLoading: assetsLoading } = useUserOwnAssets({
    relationTypes: ['representation', 'prospect'],
    isEnabled: !leadId,
  })

  const {
    matchingAssets,
    isLoading: matchingAssetsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMatchingAssets(leadId ?? '')

  const useMatchScoreMode = !!leadId && matchingAssets.length > 0

  const filteredAssets = useMemo(
    () => assets?.filter((asset) => (asset.prices || [])?.length !== 0),
    [assets]
  )

  const filteredMatchingAssets = useMemo(
    () => matchingAssets.filter((item) => (item.asset.prices || [])?.length !== 0),
    [matchingAssets]
  )

  const isLoading = matchingAssetsLoading || assetsLoading

  const disabledAssetIds = useMemo(() => {
    const invalidMatchAssetIds =
      leadId && matchingAssets && matchingAssets.length > 0
        ? matchingAssets
            .filter((item) => !validateLeadMatchScores(item.matchScores))
            .map((item) => item.asset.id)
        : []

    const allDisabledIds = [...invalidMatchAssetIds, ...(rejectedAssetIds ?? [])]
    return [...new Set(allDisabledIds)]
  }, [leadId, matchingAssets, rejectedAssetIds])

  return (
    <View className={cn('gap-4', className)}>
      <Label className="text-center text-base">{t(($) => $.detail.offeredAsset)}</Label>
      <View className="relative">
        {!!offerAssetId && allowReset && (
          <CloseButton
            className="absolute -left-3.5 -top-5 z-10 border-2 border-accent bg-white"
            iconClassName="text-accent"
            onPress={() => {
              reset()
              return false
            }}
          />
        )}
        <PropertySelect
          modalKey="lead-offered-asset-select"
          assets={useMatchScoreMode ? filteredMatchingAssets : filteredAssets}
          isLoading={isLoading}
          selectedAssetIds={offerAssetId ? [offerAssetId] : []}
          onChangeSelectedAssetIds={(selectedAssetIds) => onSetOfferedAsset(selectedAssetIds[0])}
          showCreateButton={!offerAssetId && size === 'default'}
          size={size}
          label={t(($) => $.detail.selectAsset)}
          sheetTitle={t(($) => $.detail.selectAssetTitle)}
          triggerClassName="bg-white hover:bg-white/50 active:bg-white"
          onFetchNextPage={() => void fetchNextPage()}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          disabledAssetIds={disabledAssetIds}
        />
      </View>
    </View>
  )
}
